import { useCallback, useState } from "react";
import {
  type FinalizeVaultDocumentsBatchMutation,
  type VaultDocumentAttributeValueInput,
} from "#graphql/supergraphTypes";
import { putBytesToTarget, runWithConcurrency } from "./putBytesToTarget";
import { useCreateVaultDocumentsBatch } from "./useCreateVaultDocumentsBatch";
import { useFinalizeVaultDocumentsBatch } from "./useFinalizeVaultDocumentsBatch";

type FinalizedDocument =
  FinalizeVaultDocumentsBatchMutation["finalizeVaultDocumentsBatch"]["documents"][number];

/**
 * One item's outcome, positionally aligned with the `items` passed in.
 *
 * `finalizeVaultDocumentsBatch` finalizes per document: it can report `failed`
 * entries while the round trip itself succeeds. Returning only the finalized
 * `documents` would tell the caller every file worked and hand it a shorter array
 * than it sent — so each item reports its own verdict, and `error` is set for a
 * document the batch could not finalize.
 */
export interface BatchUploadResult {
  publicRefId: string;
  /** VALIDATED / QUARANTINED — the server's verdict. Absent when `error` is set. */
  status?: FinalizedDocument["status"];
  /** Why this document was not finalized. Absent on success. */
  error?: string;
}

/** One file plus the typed attribute values it should be filed with. */
export interface BatchUploadItem {
  spaceId: string;
  file: File;
  /** Typed attribute values (see `buildAttributeValueInputs`). */
  attributeValues?: VaultDocumentAttributeValueInput[];
}

export interface UploadVaultDocumentsBatchArgs {
  items: BatchUploadItem[];
  /**
   * Fraction of the files whose bytes have finished uploading (0–1), fired as
   * each file's single PUT lands. Batch is single-part per file, so this is
   * file-granular, not byte-granular.
   */
  onProgress?: (fraction: number) => void;
}

export interface UseUploadVaultDocumentsBatchReturn {
  /**
   * Full bulk upload: `createVaultDocumentsBatch` -> PUT every file's bytes to its
   * paired presigned target -> `finalizeVaultDocumentsBatch`. Resolves to one
   * {@link BatchUploadResult} per item, in the order they were passed; a file whose
   * bytes or finalize failed carries `error` instead of `status`.
   *
   * Rejects ONLY when a whole round trip fails — the create or finalize call itself.
   * A single file's PUT failing does not sink the batch: the files that landed are
   * still finalized and reported, matching the per-item contract above.
   */
  uploadDocuments: (args: UploadVaultDocumentsBatchArgs) => Promise<BatchUploadResult[]>;
  uploading: boolean;
  error: Error | null;
}

/**
 * DV-250 — orchestrates a MULTI-FILE upload in three round trips: create the whole
 * batch, PUT every file's bytes to its paired single-part target, then finalize
 * the batch. A single file still goes through {@link useUploadVaultDocument}; this
 * hook is the 2+ path so the client makes one create + one finalize call instead
 * of one pair per file.
 *
 * @example
 * const { uploadDocuments, uploading } = useUploadVaultDocumentsBatch();
 * await uploadDocuments({ items: [{ spaceId, file, attributeValues }, ...] });
 */
export const useUploadVaultDocumentsBatch = (): UseUploadVaultDocumentsBatchReturn => {
  const { createVaultDocumentsBatch } = useCreateVaultDocumentsBatch();
  const { finalizeVaultDocumentsBatch } = useFinalizeVaultDocumentsBatch();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const uploadDocuments = useCallback(
    async ({ items, onProgress }: UploadVaultDocumentsBatchArgs) => {
      setUploading(true);
      setError(null);
      try {
        const created = await createVaultDocumentsBatch({
          inputs: items.map((it) => ({
            spaceId: it.spaceId,
            filename: it.file.name,
            contentType: it.file.type || "application/octet-stream",
            sizeBytes: it.file.size,
            attributeValues: it.attributeValues ?? [],
          })),
        });
        if (!created) throw new Error("createVaultDocumentsBatch returned no data");

        const { uploadTargets } = created;
        if (uploadTargets.length !== items.length) {
          throw new Error(
            `createVaultDocumentsBatch returned ${uploadTargets.length} targets ` +
              `for ${items.length} files`,
          );
        }

        // Targets pair with inputs by index. Every file's bytes go up through the
        // shared bounded queue — serially was one round trip per file, which undoes
        // the point of batching — and each PUT's outcome is kept per index.
        const putOutcomes = await runWithConcurrency(
          items,
          (item, index) => putBytesToTarget(uploadTargets[index], item.file, item.file.name),
          (completed, total) => onProgress?.(completed / total),
        );

        // Finalize the files that actually landed. Throwing on the first failed PUT
        // would leave every successful upload as an unfinalized document with no
        // client-side record of it — the caller would get an exception and no verdicts.
        const landed = uploadTargets
          .map((target, index) => ({ target, index }))
          .filter(({ index }) => putOutcomes[index] === null);

        const finalized = landed.length
          ? await finalizeVaultDocumentsBatch(
              landed.map(({ target }) => ({
                publicRefId: target.publicRefId,
                ssbVersionNumber: target.ssbVersionNumber,
              })),
            )
          : { documents: [], failed: [] };
        if (!finalized) throw new Error("finalizeVaultDocumentsBatch returned no data");

        // Pair every item back to its verdict. Successes are matched by
        // `publicRefId` rather than by position — the response's `documents` only
        // carries the ones that finalized, so index alignment would drift the moment
        // one fails. `failed[].index` indexes the finalize inputs, i.e. `landed`.
        const documentByRef = new Map(finalized.documents.map((d) => [d.publicRefId, d]));
        const reasonByItemIndex = new Map(
          finalized.failed.map((f) => [landed[f.index]?.index, f.reason]),
        );
        return uploadTargets.map((target, index): BatchUploadResult => {
          const putFailure = putOutcomes[index];
          if (putFailure) {
            return { publicRefId: target.publicRefId, error: putFailure.message };
          }
          const document = documentByRef.get(target.publicRefId);
          if (document) {
            return { publicRefId: document.publicRefId, status: document.status };
          }
          return {
            publicRefId: target.publicRefId,
            // A document in neither list is still a failure — the batch didn't file
            // it, and reporting it as a success is the worse guess.
            error: reasonByItemIndex.get(index) ?? "The document was not finalized",
          };
        });
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err);
        throw err;
      } finally {
        setUploading(false);
      }
    },
    [createVaultDocumentsBatch, finalizeVaultDocumentsBatch],
  );

  return { uploadDocuments, uploading, error };
};
